/**
 * The Orcaloops Terms of Service page body, rendered by the public
 * `orcaloops.ai/terms` route (`routes/orca/terms.tsx`). Kept out of the route
 * module so tests render it directly instead of reaching through the Route
 * object.
 *
 * @since 1.0.0
 */
import type { ReactNode } from "react";

import {
  orcaLegalLinkClass,
  orcaLegalListClass,
  OrcaLegalPageLayout,
  OrcaLegalSection,
} from "~/components/orca/OrcaLegalPageLayout.tsx";

/** The support mailbox, rendered as a mailto link. */
const supportEmail = <a href="mailto:support@consinna.ai" className={orcaLegalLinkClass}>support@consinna.ai</a>;

export function OrcaTermsPage(): ReactNode {
  return (
    <OrcaLegalPageLayout
      title="Orcaloops Terms of Service"
      lastUpdated="12 September 2026"
      version="1.0"
      current="/orca/terms"
      testId="orca-terms-page"
    >
      <OrcaLegalSection title="1. Agreement">
        <p>
          These terms are between you (or the entity you represent) and Consinna LLC, a California limited liability
          company, and govern your use of Orcaloops at orcaloops.ai.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="2. The service">
        <p>
          Orcaloops orchestrates coding agents against repositories and issue trackers you connect, and returns pull
          requests and run evidence.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="3. Your account">
        <p>
          You must provide accurate information, keep your credentials secure, and are responsible for activity under
          your account. You must be at least 16.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="4. Your repositories and connected services">
        <p>
          You represent that you are authorized to grant Orcaloops access to the repositories, issue trackers and
          credentials you connect, and you may revoke that access at any time.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="5. Your content">
        <p>
          You retain all rights in your code, issues and any output produced from them. You grant us only the licence
          needed to operate the service for you.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="6. Acceptable use">
        <ul className={orcaLegalListClass}>
          <li>No unlawful use of the service.</li>
          <li>No attempts to breach or overload the service.</li>
          <li>No use of the service to build or operate a competing orchestration service.</li>
          <li>No circumventing usage limits.</li>
        </ul>
      </OrcaLegalSection>

      <OrcaLegalSection title="7. AI output disclaimer">
        <p>
          Orcaloops uses large language models; generated code and text may be incorrect. You are responsible for
          reviewing every change before you merge or deploy it.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="8. Fees">
        <p>
          Where a paid plan applies, fees and billing terms are those presented at purchase. Fees are non-refundable
          except where required by law.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="9. Third-party services">
        <p>
          GitHub, Linear, Google and the LLM providers are governed by their own terms; we are not responsible for them.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="10. Warranty disclaimer">
        <p>
          The service is provided &ldquo;as is&rdquo; without warranties of any kind, to the maximum extent permitted by
          law.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="11. Limitation of liability">
        <p>
          To the maximum extent permitted by law, Consinna LLC is not liable for indirect, incidental or consequential
          damages, and its total liability is capped at the amounts you paid in the twelve months before the claim.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="12. Termination">
        <p>
          Either party may terminate at any time; we may suspend accounts that violate these terms.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="13. Governing law">
        <p>These terms are governed by California law, with venue in California.</p>
      </OrcaLegalSection>

      <OrcaLegalSection title="14. Changes">
        <p>
          We may update these terms and will revise the &ldquo;last updated&rdquo; date above.
        </p>
      </OrcaLegalSection>

      <OrcaLegalSection title="15. Contact">
        <p>Questions about these terms: {supportEmail}.</p>
      </OrcaLegalSection>
    </OrcaLegalPageLayout>
  );
}
